// Libraries
import { withScope, captureException, addBreadcrumb, captureMessage, SeverityLevel } from "@sentry/vue";

type NetworkInformationT = {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
};

type NavigatorUADataT = {
  platform: string;
  brands: Array<{ brand: string; version: string }>;
  mobile: boolean;
};

declare global {
  interface Navigator {
    connection?: NetworkInformationT;
    userAgentData?: NavigatorUADataT;
  }
}

export class SentryService {
  constructor() {
    this.setupGlobalErrorHandlers();
  }

  private parseQueryParams(url?: string): Record<string, string> | null {
    if (!url) return null;

    try {
      const queryString = url.includes("?") ? url.split("?")[1] : null;

      if (!queryString) return null;

      const cleanQuery = queryString.includes("#") ? queryString.split("#")[0] : queryString;

      const params: Record<string, string> = {};
      const searchParams = new URLSearchParams(cleanQuery);

      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }

      return Object.keys(params).length > 0 ? params : null;
    } catch (e) {
      console.error("Erro ao analisar parâmetros de query:", e);

      return null;
    }
  }

  private sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
    const sanitized = { ...headers };
    const sensitiveHeaders = ["authorization", "cookie", "x-auth-token"];

    for (const header of sensitiveHeaders) {
      if (header.toLowerCase() in sanitized) {
        sanitized[header] = "[REDACTED]";
      }
    }

    return sanitized;
  }

  private sanitizePayload(data: Record<string, unknown> | unknown[] | unknown): any {
    if (data === null || data === undefined) return data;

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizePayload(item));
    }

    if (typeof data === "object" && data !== null) {
      const sensitiveFields = ["password", "token", "secret", "credit_card", "cvv", "pin", "cpf", "cnpj", "cpf_cnpj"];
      const sanitized: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        if (sensitiveFields.includes(key)) {
          sanitized[key] = "[REDACTED]";
        } else if (Array.isArray(value)) {
          sanitized[key] = this.sanitizePayload(value);
        } else if (typeof value === "object" && value !== null) {
          sanitized[key] = this.sanitizePayload(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    }

    return data;
  }

  setupGlobalErrorHandlers(): void {
    window.addEventListener("unhandledrejection", event => {
      const errorInfo = this.extractErrorInfo(event.reason);
      
      console.error("[Global Error] Unhandled Promise Rejection:", {
        reason: event.reason,
        errorInfo,
        promise: event.promise,
        timestamp: new Date().toISOString()
      });
      
      withScope(scope => {
        scope.setTag("Error Type", "Unhandled Promise Rejection");
        scope.setTag("Error Source", errorInfo.source || "unknown");
        scope.setExtra("Reason", event.reason);
        scope.setExtra("Error Info", errorInfo);
        scope.setExtra("Promise", String(event.promise));
        scope.setExtra("User Agent", navigator.userAgent);
        scope.setExtra("URL", window.location.href);
        scope.setExtra("Timestamp", new Date().toISOString());
        
        // Adicionar fingerprint customizado para agrupar erros similares
        const fingerprint = ["unhandled-promise", errorInfo.type || "unknown"];
        if (errorInfo.source) fingerprint.push(errorInfo.source);
        scope.setFingerprint(fingerprint);
        
        captureException(event.reason);
      });
    });

    // Handler principal de erros JavaScript
    window.addEventListener("error", event => {
      // Se for erro de recurso, pular (será tratado pelo outro listener)
      if (event.target && event.target !== window) return;
      
      const errorInfo = this.extractErrorInfo(event.error);
      
      console.error("[Global Error] Window Error:", {
        error: event.error,
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        errorInfo,
        timestamp: new Date().toISOString()
      });
      
      withScope(scope => {
        scope.setTag("Error Type", "Global Error");
        scope.setTag("Error Source", errorInfo.source || "script");
        scope.setExtra("Message", event.message);
        scope.setExtra("Filename", event.filename);
        scope.setExtra("Line Number", event.lineno);
        scope.setExtra("Column Number", event.colno);
        scope.setExtra("Error Info", errorInfo);
        scope.setExtra("User Agent", navigator.userAgent);
        scope.setExtra("URL", window.location.href);
        scope.setExtra("Timestamp", new Date().toISOString());
        
        // Adicionar informações sobre o script
        if (event.filename) {
          const isExternal = !event.filename.includes(window.location.origin);
          scope.setTag("Script Type", isExternal ? "external" : "internal");
          
          if (isExternal) {
            scope.setTag("External Script", new URL(event.filename).hostname);
          }
        }
        
        // Adicionar fingerprint customizado
        const fingerprint = ["window-error", errorInfo.type || "unknown"];
        if (event.filename) {
          const filename = event.filename.split('/').pop() || 'unknown';
          fingerprint.push(filename);
        }
        scope.setFingerprint(fingerprint);
        
        captureException(event.error || new Error(event.message));
      });
    });
    
    // Listener separado para erros de recursos (imagens, scripts, etc)
    window.addEventListener("error", event => {
      // Verificar se é erro de recurso
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        
        console.warn("[Global Error] Resource Load Error:", {
          type: target.tagName,
          src: (target as any).src || (target as any).href,
          timestamp: new Date().toISOString()
        });
        
        withScope(scope => {
          scope.setTag("Error Type", "Resource Load Error");
          scope.setTag("Resource Type", target.tagName);
          scope.setLevel("warning");
          
          const resourceUrl = (target as any).src || (target as any).href;
          scope.setExtra("Resource URL", resourceUrl);
          scope.setExtra("Page URL", window.location.href);
          scope.setExtra("Timestamp", new Date().toISOString());
          
          captureMessage(`Failed to load ${target.tagName}: ${resourceUrl}`, "warning");
        });
      }
    }, true);
  }
  
  private extractErrorInfo(error: unknown): Record<string, any> {
    const info: Record<string, any> = {
      type: "unknown",
      message: "Unknown error",
      source: null
    };
    
    if (!error) return info;
    
    if (error instanceof Error) {
      info.type = error.constructor.name;
      info.message = error.message;
      
      // Tentar extrair fonte do erro do stack trace
      if (error.stack) {
        const stackLines = error.stack.split('\n');
        if (stackLines.length > 1) {
          const match = stackLines[1].match(/at\s+(.+?)\s+\(/);
          if (match && match[1]) {
            info.source = match[1].trim();
          }
        }
      }
      
      // Detectar tipos específicos de erro
      if (error.message.includes('Cannot read properties of undefined')) {
        info.category = "null-reference";
      } else if (error.message.includes('Cannot read properties of null')) {
        info.category = "null-reference";
      } else if (error.message.includes('is not a function')) {
        info.category = "type-error";
      } else if (error.message.includes('Network')) {
        info.category = "network";
      }
    } else if (typeof error === 'string') {
      info.type = "string";
      info.message = error;
    } else if (typeof error === 'object' && error !== null) {
      info.type = "object";
      info.message = JSON.stringify(error);
      info.data = error;
    }
    
    return info;
  }

  addBreadcrumb(category: string, message: string, data?: Record<string, any>, level: SeverityLevel = "info"): void {
    addBreadcrumb({
      category,
      message,
      data,
      level: level as SeverityLevel,
      timestamp: Date.now() / 1000
    });
  }

  captureReplay(message: string, data: Record<string, any>): void {
    withScope(scope => {
      scope.setContext("Replay Data", data);
      scope.setTag("Replay", "true");
      captureMessage(message, "info");
    });
  }

  private createCustomFingerprint(error: unknown, errorCode?: string, params?: Record<string, string>): string[] {
    const fingerprint = ["{{ default }}"];

    if (errorCode) {
      fingerprint.push(`errorCode:${errorCode}`);

      if (params?.endpoint) {
        try {
          const endpoint = params.endpoint.split("/").filter(Boolean)[0] || "unknown";

          fingerprint.push(`api:${endpoint}`);
        } catch {
          fingerprint.push(`api:unknown`);
        }
      }

      const statusCode = this.extractStatusCode(error);

      if (statusCode) {
        const statusCategory = Math.floor(statusCode / 100);

        fingerprint.push(`statusCategory:${statusCategory}xx`);
      }

      return fingerprint;
    }

    if (this.isNetworkError(error)) {
      fingerprint.push("type:network-error");

      return fingerprint;
    }

    return fingerprint;
  }

  private extractStatusCode(error: unknown): number | null {
    if (typeof error === "object" && error !== null) {
      const errorObj = error as Record<string, any>;

      if (
        "response" in errorObj &&
        errorObj.response &&
        typeof errorObj.response === "object" &&
        "status" in errorObj.response &&
        typeof errorObj.response.status === "number"
      ) {
        return errorObj.response.status;
      }
    }

    return null;
  }

  private isNetworkError(error: unknown): boolean {
    if (typeof error === "object" && error !== null) {
      const errorObj = error as Record<string, any>;

      if ("request" in errorObj && (!errorObj.response || !("status" in errorObj.response))) {
        return true;
      }

      if (errorObj.code === "ECONNABORTED" || errorObj.code === "ETIMEDOUT" || errorObj.code === "ERR_NETWORK") {
        return true;
      }
    }

    return false;
  }

  private createErrorTitle(
    error: unknown,
    errorCode?: string,
    errorMessage?: { title?: string; message?: string }
  ): string {
    let title = "";

    const statusCode = this.extractStatusCode(error);

    if (statusCode) {
      title += `[${statusCode}] `;

      if (statusCode >= 500) {
        title += "Erro de Servidor";
      } else if (statusCode >= 400) {
        title += "Erro de Requisição";
      } else {
        title += "Resposta Inesperada";
      }
    } else if (this.isNetworkError(error)) {
      title = "Erro de Rede";
    } else if (errorCode) {
      title = `[${errorCode}] `;
      title += errorMessage?.title || "Erro";
    } else {
      title = errorMessage?.title || "Erro Desconhecido";
    }

    return title;
  }

  captureRequestData(
    request: {
      url?: string;
      method?: string;
      headers?: Record<string, string>;
      params?: Record<string, string>;
      data?: Record<string, unknown>;
    }
  ): void {
    // Validação defensiva para evitar erros espúrios
    if (!request || typeof request !== 'object') {
      console.warn('[SentryService] captureRequestData chamado com request inválido:', request);
      return;
    }
    
    withScope(scope => {
      const sanitizedPayload = this.sanitizePayload(request.data || {});
      const payloadStr = JSON.stringify(sanitizedPayload, null, 2);
      const paramsStr = request.params ? JSON.stringify(request.params, null, 2) : "{}";

      scope.setContext("Detalhes da Requisicao", {
        url: request.url,
        method: request.method,
        headers: this.sanitizeHeaders(request.headers || {}),
        params_json: paramsStr,
        payload_json: payloadStr
      });

      const queryParams = this.parseQueryParams(request.url);

      if (queryParams) {
        const queryParamsStr = JSON.stringify(queryParams, null, 2);
        scope.setContext("Parametros de Query", {
          url: request.url,
          params_json: queryParamsStr
        });
      }

      // Removido captureMessage("API Request") - não é um erro ou warning
    });
  }

  captureNetworkError(
    error: {
      request?: {
        readyState?: number;
        status?: number;
        responseURL?: string;
        getAllResponseHeaders?: () => Record<string, string>;
      };
      response?: { status?: number; statusText?: string; data?: Record<string, unknown> };
    },
    shouldCapture: boolean = true
  ): void {
    // Validação defensiva para evitar erros espúrios
    if (!error || typeof error !== 'object') {
      console.warn('[SentryService] captureNetworkError chamado com error inválido:', error);
      return;
    }
    
    withScope(scope => {
      scope.setTag("Tipo de Erro", "Rede");
      scope.setTag("Status da Conexao", navigator.onLine ? "online" : "offline");

      scope.setContext("Detalhes da Conexao", {
        type: navigator.connection?.type,
        effectiveType: navigator.connection?.effectiveType,
        downlink: navigator.connection?.downlink,
        rtt: navigator.connection?.rtt
      });

      scope.setContext("Detalhes da Requisicao", {
        readyState: error.request?.readyState,
        status: error.request?.status,
        responseURL: error.request?.responseURL,
        responseHeaders: error.request?.getAllResponseHeaders?.() || null
      });

      const responseData = this.sanitizePayload(error.response?.data || {});
      const responseDataStr = JSON.stringify(responseData, null, 2);

      scope.setContext("Detalhes da Resposta", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data_json: responseDataStr
      });

      if (shouldCapture) {
        const errorTitle = this.createErrorTitle(error);
        const errorObj = new Error(errorTitle);

        if (error instanceof Error) {
          errorObj.stack = error.stack;
          (errorObj as unknown as Error).cause = error;
        }

        const endpoint = (() => {
          try {
            return error.request?.responseURL
              ? new URL(error.request.responseURL, window.location.origin).pathname
              : undefined;
          } catch {
            return undefined;
          }
        })();

        const fingerprint = this.createCustomFingerprint(error, "network-error", endpoint ? { endpoint } : undefined);

        scope.setFingerprint(fingerprint);

        captureException(errorObj);
      }
    });
  }

  captureError(
    error: unknown,
    errorCode?: string,
    errorMessage?: { title?: string; message?: string },
    params?: Record<string, string>,
    userInfo?: { id?: string; email?: string; name?: string; teamId?: string; projectId?: string; role?: string }
  ): void {
    if (errorCode === "network-error") return;
    
    // Validação defensiva para evitar erros espúrios
    if (error === undefined || error === null) {
      console.warn('[SentryService] captureError chamado com error undefined/null');
      return;
    }

    withScope(scope => {
      let requestBody: Record<string, unknown> | null = null;

      if (typeof error === "object" && error !== null) {
        const errorObj = error as Record<string, any>;

        if ("data" in errorObj) {
          requestBody = errorObj.data as Record<string, unknown>;
        }
      }

      if (params?.error_source) {
        scope.setTag("error_source", params.error_source);
      }

      scope.setTag("Codigo do Erro", errorCode || "unknown");

      const responseStr = errorMessage ? JSON.stringify(errorMessage, null, 2) : "null";
      const requestStr = requestBody ? JSON.stringify(this.sanitizePayload(requestBody), null, 2) : "{}";

      scope.setExtra("Dados da resposta (JSON)", responseStr);
      scope.setExtra("Dados da requisicao (JSON)", requestStr);

      scope.setTag("Origem do Erro", "API");
      scope.setTag("Ambiente", import.meta.env?.MODE || "unknown");
      scope.setTag("Versao da Aplicacao", import.meta.env?.VITE_APP_VERSION || "unknown");

      scope.setContext("Timestamp", {
        time: new Date().toISOString(),
        localTime: new Date().toString(),
        unixTime: Date.now()
      });

      scope.setContext("Informacoes do Navegador", {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.userAgentData?.platform || navigator.platform || "unknown"
      });

      scope.setContext("Informacoes do Dispositivo", {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        pixelRatio: window.devicePixelRatio,
        orientation: window.screen.orientation?.type || "unknown"
      });

      try {
        const route = window.location;

        if (route) {
          scope.setContext("Rota Atual", {
            path: route.pathname,
            href: route.href,
            search: route.search,
            hash: route.hash
          });
        }
      } catch (e) {
        // NOTE: Silenciar erro se a rota não estiver disponível
      }

      if (userInfo) {
        scope.setUser({
          id: userInfo.id,
          email: userInfo.email,
          username: userInfo.name
        });

        scope.setContext("Contexto do Usuario", {
          teamId: userInfo.teamId,
          projectId: userInfo.projectId,
          role: userInfo.role
        });
      }

      if (params) {
        scope.setContext("Parametros Personalizados", params);
      }

      if (typeof error === "object" && error !== null) {
        const errorObj = error as Record<string, any>;

        if ("request" in errorObj || "response" in errorObj) {
          const networkError = error as {
            request?: {
              readyState?: number;
              status?: number;
              responseURL?: string;
              getAllResponseHeaders?: () => Record<string, string>;
            };
            response?: { status?: number; statusText?: string; data?: Record<string, unknown> };
            data?: Record<string, unknown>;
          };

          scope.setTag("Tipo de Erro", "Rede");
          scope.setTag("Status da Conexao", navigator.onLine ? "online" : "offline");

          const queryParams = this.parseQueryParams(networkError.request?.responseURL);

          if (queryParams) {
            const queryParamsStr = JSON.stringify(queryParams, null, 2);

            scope.setContext("Parametros de Query", {
              url: networkError.request?.responseURL,
              params_json: queryParamsStr
            });
          }

          scope.setContext("Detalhes da Conexao", {
            type: navigator.connection?.type,
            effectiveType: navigator.connection?.effectiveType,
            downlink: navigator.connection?.downlink,
            rtt: navigator.connection?.rtt
          });

          const requestDetails = {
            readyState: networkError.request?.readyState,
            status: networkError.request?.status,
            responseURL: networkError.request?.responseURL,
            responseHeaders: networkError.request?.getAllResponseHeaders?.() || null
          };

          if (requestBody || networkError.data) {
            const sanitizedBody = this.sanitizePayload(requestBody || networkError.data || {});
            const bodyStr = JSON.stringify(sanitizedBody, null, 2);

            scope.setContext("Detalhes da Requisicao", {
              ...requestDetails,
              body_json: bodyStr
            });
          } else {
            scope.setContext("Detalhes da Requisicao", requestDetails);
          }

          const responseData = this.sanitizePayload(networkError.response?.data || {});
          const responseDataStr = JSON.stringify(responseData, null, 2);

          scope.setContext("Detalhes da Resposta", {
            status: networkError.response?.status,
            statusText: networkError.response?.statusText,
            data_json: responseDataStr
          });
        }

        if ("url" in errorObj || "method" in errorObj || "headers" in errorObj) {
          const requestData = error as {
            url?: string;
            method?: string;
            headers?: Record<string, string>;
            params?: Record<string, string>;
            data?: Record<string, unknown>;
          };

          const queryParams = this.parseQueryParams(requestData.url);

          if (queryParams) {
            const queryParamsStr = JSON.stringify(queryParams, null, 2);

            scope.setContext("Parametros de Query", {
              url: requestData.url,
              params_json: queryParamsStr
            });
          }

          const requestDetails = {
            url: requestData.url,
            method: requestData.method,
            headers: this.sanitizeHeaders(requestData.headers || {}),
            params: requestData.params
          };

          if (requestBody || requestData.data) {
            const sanitizedBody = this.sanitizePayload(requestBody || requestData.data || {});
            const bodyStr = JSON.stringify(sanitizedBody, null, 2);

            scope.setContext("Detalhes da Requisicao", {
              ...requestDetails,
              body_json: bodyStr
            });
          } else {
            scope.setContext("Detalhes da Requisicao", requestDetails);
          }
        }
      } else {
        scope.setTag("Tipo de Erro", "Rede");
        scope.setTag("Status da Conexao", navigator.onLine ? "online" : "offline");

        scope.setContext("Detalhes da Conexao", {
          type: navigator.connection?.type,
          effectiveType: navigator.connection?.effectiveType,
          downlink: navigator.connection?.downlink,
          rtt: navigator.connection?.rtt
        });

        const requestDetails = {
          status: 0,
          responseURL: "N/A"
        };

        if (requestBody) {
          const sanitizedBody = this.sanitizePayload(requestBody);
          const bodyStr = JSON.stringify(sanitizedBody, null, 2);

          scope.setContext("Detalhes da Requisicao", {
            ...requestDetails,
            body_json: bodyStr
          });
        } else {
          scope.setContext("Detalhes da Requisicao", requestDetails);
        }

        const errorData = this.sanitizePayload({ mensagem: errorMessage?.message || "Sem detalhes" });
        const errorDataStr = JSON.stringify(errorData, null, 2);

        scope.setContext("Detalhes da Resposta", {
          status: 500,
          statusText: errorMessage?.title || "Erro Desconhecido",
          data_json: errorDataStr
        });
      }

      const errorTitle = this.createErrorTitle(error, errorCode, errorMessage);

      let errorDescription = "";

      if (errorMessage?.message) {
        errorDescription = errorMessage.message;
      } else if (error instanceof Error) {
        errorDescription = error.message;
      }

      const finalErrorName = errorDescription ? `${errorTitle}: ${errorDescription}` : errorTitle;

      const errorObj = error instanceof Error ? new Error(finalErrorName) : new Error(finalErrorName);

      if (error instanceof Error && error.stack) {
        errorObj.stack = error.stack;
      }

      if (error instanceof Error) {
        (errorObj as any).cause = error;
      }

      const fingerprint = this.createCustomFingerprint(error, errorCode, params);

      scope.setFingerprint(fingerprint);

      captureException(errorObj);
    });
  }
}

export const sentryService = new SentryService();
